import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api/axiosConfig';
import MathText from '../components/MathText';
import { useAuth } from '../context/AuthContext';

function CompositeTestPage() {
  const { classLevel, lang } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [displayQuestion, setDisplayQuestion] = useState(null);
  
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  const [isSending, setIsSending] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);

  // Карта перевода параметра URL в реальные названия предметов в вашей БД
  const langMap = {
    'english': 'Английский язык',
    'german': 'Немецкий язык',
    'french': 'Французский язык'
  };

  useEffect(() => {
    const fetchCompositeExam = async () => {
      setLoading(true);
      try {
        // 1. Сначала получаем список всех предметов, чтобы узнать их ID
        const subjectsRes = await API.get('/api/subjects/');
        const allSubjects = subjectsRes.data;

        const rusSubject = allSubjects.find(s => s.name === 'Русский язык');
        const mathSubject = allSubjects.find(s => s.name === 'Математика');
        const foreignSubject = allSubjects.find(s => s.name === langMap[lang]);

        if (!rusSubject || !mathSubject || !foreignSubject) {
          setErrorMsg("В базе данных не найден один из обязательных предметов (Русский, Математика или Иностранный).");
          setLoading(false);
          return;
        }

        // 2. Скачиваем вопросы для всех трех предметов одновременно
        const [rusRes, mathRes, foreignRes] = await Promise.all([
          API.get(`/api/questions/?subject=${rusSubject.id}&class_level=${classLevel}`),
          API.get(`/api/questions/?subject=${mathSubject.id}&class_level=${classLevel}`),
          API.get(`/api/questions/?subject=${foreignSubject.id}&class_level=${classLevel}`)
        ]);

        // 3. Перемешиваем и отрезаем точное количество: 30 + 25 + 30
        const rusQuestions = rusRes.data.sort(() => 0.5 - Math.random()).slice(0, 30).map(q => ({ ...q, subjectName: 'Русский язык' }));
        const mathQuestions = mathRes.data.sort(() => 0.5 - Math.random()).slice(0, 25).map(q => ({ ...q, subjectName: 'Математика' }));
        const foreignQuestions = foreignRes.data.sort(() => 0.5 - Math.random()).slice(0, 30).map(q => ({ ...q, subjectName: langMap[lang] }));

        // 4. Склеиваем в один большой тест
        const combinedQuestions = [...rusQuestions, ...mathQuestions, ...foreignQuestions];
        
        if (combinedQuestions.length === 0) {
          setErrorMsg(`Нет доступных вопросов для ${classLevel} класса.`);
          setLoading(false);
          return;
        }

        setQuestions(combinedQuestions);
        setDisplayQuestion(combinedQuestions[0]);
        setLoading(false);

      } catch (err) {
        console.error("Ошибка при сборке экзамена:", err);
        setErrorMsg("Произошла ошибка при загрузке данных с сервера.");
        setLoading(false);
      }
    };

    fetchCompositeExam();
  }, [classLevel, lang]);

  // Обновление формул MathJax
  useEffect(() => {
    if (window.MathJax && displayQuestion && !loading) {
      window.MathJax.typesetPromise()
        .then(() => setIsTransitioning(false))
        .catch(() => setIsTransitioning(false));
    } else {
        setIsTransitioning(false);
    }
  }, [displayQuestion, loading]);

  const handleAnswerClick = async (ans) => {
    if (isTransitioning) return;

    const labels = ['А', 'Б', 'В', 'Г', 'Д'];
    const correctIdx = displayQuestion.answers.findIndex(a => a.is_correct);
    const studentIdx = displayQuestion.answers.findIndex(a => a.id === ans.id);

    const currentAnswerData = {
      q_num: currentQuestionIndex + 1,
      correct: labels[correctIdx] || '?',
      student: labels[studentIdx] || '?',
      is_correct: ans.is_correct,
      question_id: displayQuestion.id,
      answer_id: ans.id
    };
    
    const newUserAnswers = [...userAnswers, currentAnswerData];
    setUserAnswers(newUserAnswers);

    const newScore = ans.is_correct ? score + 1 : score;
    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex < questions.length) {
      setIsTransitioning(true);
      setTimeout(() => {
        setScore(newScore);
        setCurrentQuestionIndex(nextIndex);
        setDisplayQuestion(questions[nextIndex]);
      }, 300);
    } else {
      // КОНЕЦ ЭКЗАМЕНА
      setScore(newScore);
      setIsSending(true);
      
      try {
        if (isAuthenticated) {
            // Отправляем общий результат.
            await API.post('/api/results/', {
              class_level: classLevel, 
              score: newScore,
              total_questions: questions.length,
              answers_list: newUserAnswers
            });
        }
        setShowResult(true);
      } catch (err) {
        console.error("Ошибка отправки:", err);
        setShowResult(true); 
      } finally {
        setIsSending(false);
      }
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
      <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
      <p className="text-slate-500 font-bold animate-pulse">Формируем экзаменационный билет...</p>
    </div>
  );
  
  if (errorMsg) return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-slate-50 text-center">
          <span className="text-5xl mb-4">⚠️</span>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{errorMsg}</h2>
          <button onClick={() => navigate('/')} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all">На главную</button>
      </div>
  );
  
  if (showResult) return (
     <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl text-center max-w-md w-full border border-slate-100">
           <div className="text-6xl mb-4">🎓</div>
           <h2 className="text-2xl font-black text-slate-800 mb-2">Экзамен завершен!</h2>
           <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-6">Выпускной • {classLevel} класс</p>
           
           <div className="bg-slate-50 p-6 rounded-2xl mb-8 border border-slate-100">
             <div className="text-6xl font-black text-purple-600 mb-2">{score} <span className="text-3xl text-slate-400">/ {questions.length}</span></div>
             <p className="text-slate-500 font-medium text-sm">Ваш итоговый результат</p>
           </div>

           <button onClick={() => navigate('/')} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold transition-colors">Вернуться на главную</button>
        </div>
     </div>
  );

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        
        {/* Хедер теста с прогресс-баром */}
        <div className="mb-8">
            <div className="flex justify-between items-end mb-2">
                <div>
                  <h1 className="text-xl font-black text-slate-900 leading-tight">Выпускной экзамен ({classLevel} класс)</h1>
                  <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded uppercase tracking-widest">Блок: {displayQuestion.subjectName}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-black text-slate-400 block mb-1">Вопрос {currentQuestionIndex + 1} из {questions.length}</span>
                  <span className="text-blue-600 font-black">{Math.round(progress)}%</span>
                </div>
            </div>
            <div className="w-full bg-slate-200 h-3 rounded-full overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
            </div>
        </div>

        {/* Карточка вопроса */}
        <div className={`bg-white rounded-[2rem] shadow-xl p-6 md:p-10 border border-slate-100 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <div className="text-lg md:text-xl font-bold text-slate-800 mb-8 leading-relaxed">
              <MathText text={displayQuestion.text} />
          </div>

          {displayQuestion.image && (
              <img src={displayQuestion.image} alt="Вопрос" className="max-h-64 object-contain rounded-xl mb-8 border border-slate-200 bg-slate-50 w-full" />
          )}

          <div className="space-y-3">
            {displayQuestion.answers && displayQuestion.answers.map((ans, i) => (
              <button
                key={i}
                disabled={isSending || isTransitioning}
                onClick={() => handleAnswerClick(ans)}
                className="w-full text-left p-4 border-2 border-slate-100 rounded-2xl hover:border-purple-500 hover:bg-purple-50 transition-all flex items-center group"
              >
                <span className="w-10 h-10 shrink-0 bg-slate-100 rounded-xl flex items-center justify-center mr-4 text-sm font-black text-slate-500 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                  {['А', 'Б', 'В', 'Г', 'Д'][i] || String.fromCharCode(65 + i)}
                </span>
                <span className="text-base font-semibold text-slate-700 group-hover:text-purple-900">
                    <MathText text={ans.text} />
                </span>
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default CompositeTestPage;